import { Controller } from "@hotwired/stimulus";
import { openPopover } from "../lib/bubble";
import { colorNameKey } from "../lib/color_name";
import { gradientCss, parseColor, parseColorValue, withAlpha } from "../lib/color_value";
import { t } from "../lib/i18n";

const STORAGE_KEY = "colorPickerRecents";
const RECENTS = 6;
const NEUTRALS = ["#000000", "#333333", "#666666", "#999999", "#cccccc", "#ffffff"];
const MAX_STOPS = 5;
const CHECKER = "repeating-conic-gradient(rgb(var(--color-surface_300)) 0 25%, transparent 0 50%) 0 0 / 8px 8px";

/**
 * Le contrôleur de Ui::ColorPickerComponent. Il tient l'état (le mode — aucune, uni,
 * dégradé —, les arrêts avec leur couleur, leur opacité et leur position, l'angle, l'arrêt en
 * cours), le rend dans le composant à partir de ses gabarits, et écrit la valeur CSS
 * (lib/color_value) dans le champ caché, qui reçoit `input` à chaque mouvement et `change` à
 * chaque choix : l'hôte y branche ses actions comme sur n'importe quel input.
 *
 * Un seul popover de couleurs pour le champ et ses arrêts : il s'ouvre contre le bouton
 * cliqué, et change la couleur de l'arrêt en cours ; il reste ouvert après un choix. Les
 * valeurs numériques (opacité, position, angle) passent par le contrôleur `range-field`,
 * dont on écoute les événements, jamais la frappe.
 *
 * Son rendu est le sien : au morph du frame qui le porte (la sous-page se recharge après
 * chaque sauvegarde), il garde ses enfants et ne suit que sa valeur (`colorValue`), qu'il
 * recharge si elle diffère de ce qu'il montre.
 */
export default class extends Controller {
  static values = {
    color: { type: String, default: "transparent" },
    palette: { type: Array, default: [] },
    allowTransparent: { type: Boolean, default: false },
    allowGradient: { type: Boolean, default: false },
  };

  static targets = [
    "input", "modes", "solid", "gradientOnly", "bar", "angle", "addStop", "stops",
    "panel", "brandGroup", "neutrals", "recentsGroup", "native",
    "chipTpl", "rowTpl", "handleTpl", "dotTpl", "noneTpl",
  ];

  /** À la connexion, et quand le serveur (morph) change la valeur : l'état suit — sauf si
   *  c'est la nôtre qui revient (commit), l'arrêt en cours resterait sinon à zéro. */
  colorValueChanged(value) {
    if (this.stops && value === this.css) return;
    this.load(value);
    this.render();
  }

  /** Le morph du frame ne touche pas à ce que le composant a rendu lui-même : ses enfants
   *  restent, seuls les attributs de la racine (sa valeur) suivent le serveur. */
  keepRendered(e) {
    if (e.target !== this.element) e.preventDefault();
  }

  disconnect() {
    this.closePanel?.();
  }

  // ==================== Le popover ====================

  /** Le bouton d'un champ (celui du mode uni, ou celui d'un arrêt) : son arrêt prend la main
   *  et le popover s'ouvre contre lui — ou se ferme, s'il y était déjà. */
  open(e) {
    const button = e.currentTarget;
    this.active = this.stopIndex(e);
    if (this.panelOpen && this.anchor === button) return this.closePanel?.();
    this.closePanel?.();
    this.anchor = button;
    this.closePanel = openPopover(button, this.panelTarget);
    this.render();
  }

  /** Le navigateur a ouvert ou fermé le popover (clic dehors, Échap) : les boutons le disent. */
  panelToggled(e) {
    if (e.newState !== "open") {
      this.closePanel = null;
      this.anchor = null;
    }
    this.renderOpenButtons();
  }

  get panelOpen() {
    return this.hasPanelTarget && this.panelTarget.matches(":popover-open");
  }

  // ==================== Actions ====================

  /** Uni ↔ Dégradé : vers le dégradé, la couleur courante en premier arrêt et le blanc en
   *  second (le noir si elle est blanche), à 180° ; vers l'uni, le premier arrêt reste. */
  setMode({ params: { mode } }) {
    if (mode === "gradient") {
      if (this.gradient) return;
      const first = this.transparent ? { hex: "#ffffff", alpha: 1 } : this.stops[0];
      const second = first.hex === "#ffffff" ? "#000000" : "#ffffff";
      this.stops = [{ ...first, offset: 0 }, { hex: second, alpha: 1, offset: 1 }];
      this.angle = 180;
      this.transparent = false;
      this.gradient = true;
    } else {
      if (!this.gradient) return;
      this.stops = [{ ...this.stops[0], offset: 0 }];
      this.gradient = false;
    }
    this.active = 0;
    this.commit();
  }

  /** Une ligne d'arrêt prend la main (clic n'importe où dessus). */
  selectStop(e) {
    const index = this.stopIndex(e);
    if (index === this.active) return;
    this.active = index;
    this.render();
  }

  /** Un arrêt de plus, entre l'arrêt choisi et le suivant (ou le précédent, s'il est le
   *  dernier), de la même couleur. */
  addStop() {
    if (!this.gradient || this.stops.length >= MAX_STOPS) return;
    const current = this.stops[this.active];
    const neighbour = this.stops[this.active + 1] ?? this.stops[this.active - 1] ?? current;
    const offset = Math.round(((current.offset + neighbour.offset) / 2) * 100) / 100;
    this.stops.push({ ...current, offset });
    this.active = this.stops.length - 1;
    this.commit();
  }

  /** Le « − » d'une ligne : son arrêt part ; jamais sous deux arrêts. */
  removeStop(e) {
    e.stopPropagation();
    if (!this.gradient || this.stops.length <= 2) return;
    const index = this.stopIndex(e);
    this.stops.splice(index, 1);
    if (this.active >= this.stops.length || index < this.active) this.active = Math.max(0, this.active - 1);
    this.commit();
  }

  /** Une couleur de la palette (`data-color-picker-color-param`, ou "transparent"). */
  pick({ params: { color } }) {
    if (color === "transparent") {
      this.transparent = true;
      this.gradient = false;
      this.stops = [{ ...this.stops[0], offset: 0 }];
      this.active = 0;
    } else {
      this.setActiveHex(color);
    }
    this.commit();
  }

  nativeInput(e) {
    this.setActiveHex(e.target.value);
    this.commit({ live: true });
  }

  nativeChange(e) {
    this.setActiveHex(e.target.value);
    this.remember(e.target.value);
    this.commit();
  }

  alphaInput(e) {
    this.stops[this.stopIndex(e)].alpha = Number(e.target.value) / 100;
    this.commit({ live: true });
  }

  alphaChange(e) {
    this.stops[this.stopIndex(e)].alpha = Number(e.target.value) / 100;
    this.commit();
  }

  posInput(e) {
    this.active = this.stopIndex(e);
    this.stops[this.active].offset = Number(e.target.value) / 100;
    this.commit({ live: true });
  }

  posChange(e) {
    this.active = this.stopIndex(e);
    this.stops[this.active].offset = Number(e.target.value) / 100;
    this.commit();
  }

  angleInput() {
    this.angle = Number(this.angleTarget.value);
    this.commit({ live: true });
  }

  angleChange() {
    this.angle = Number(this.angleTarget.value);
    this.commit();
  }

  /** Un curseur de la barre : l'arrêt suit le pointeur, de 0 à 100 %. */
  handleDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    const index = Number(e.currentTarget.dataset.index);
    this.active = index;
    const rect = this.barTarget.getBoundingClientRect();
    const move = (ev) => {
      const ratio = (ev.clientX - rect.left) / rect.width;
      this.stops[index].offset = Math.round(Math.max(0, Math.min(1, ratio)) * 100) / 100;
      this.commit({ live: true });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      this.commit();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    this.render();
  }

  /** Reflète une couleur venue de l'éditeur (detail.fill d'un événement window : la plage
   *  sélectionnée d'un texte), sans rien émettre. Un dégradé en place, ou une couleur
   *  mixte, laisse le panneau tel quel. */
  reflect(e) {
    const fill = e.detail?.fill;
    if (typeof fill !== "string" || this.gradient) return;
    if (fill !== "transparent" && !parseColor(fill)) return;
    this.inputTarget.value = fill;
    this.colorValue = fill;
  }

  // ==================== État ====================

  /** L'arrêt d'un événement : celui de la ligne où il a eu lieu, le premier hors ligne. */
  stopIndex(e) {
    return Number(e.target.closest("[data-stop]")?.dataset.index ?? 0);
  }

  load(css) {
    const value = parseColorValue(css);
    this.transparent = value.kind === "transparent";
    this.gradient = value.kind === "gradient";
    this.angle = value.kind === "gradient" ? value.gradient.angle : 180;
    const colors = value.kind === "gradient"
      ? value.gradient.stops
      : [{ color: value.kind === "solid" ? value.color : "#000000", offset: 0 }];
    this.stops = colors.map(({ color, offset }) => ({ ...(parseColor(color) || { hex: "#000000", alpha: 1 }), offset }));
    this.active = 0;
  }

  get css() {
    if (this.transparent) return "transparent";
    if (!this.gradient) return withAlpha(this.stops[0].hex, this.stops[0].alpha);
    return gradientCss({ angle: this.angle, stops: this.sortedStops().map(({ hex, alpha, offset }) => ({ color: withAlpha(hex, alpha), offset })) });
  }

  sortedStops() {
    return [...this.stops].sort((a, b) => a.offset - b.offset);
  }

  setActiveHex(hex) {
    this.transparent = false;
    this.stops[this.active] = { ...this.stops[this.active], hex: hex.toLowerCase() };
  }

  /** Écrit la valeur dans le champ et prévient l'hôte : `input` toujours, `change` à un choix
   *  — et à un choix, les arrêts se rangent par position. */
  commit({ live = false } = {}) {
    if (!live && this.gradient) {
      const current = this.stops[this.active];
      this.stops = this.sortedStops();
      this.active = this.stops.indexOf(current);
    }
    const css = this.css;
    this.colorValue = css;
    this.inputTarget.value = css;
    this.render();
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    if (!live) this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // ==================== Rendu ====================

  render() {
    if (this.hasModesTarget) {
      this.modesTarget.dataset.mode = this.gradient ? "gradient" : "solid";
      for (const button of this.modesTarget.querySelectorAll("[data-color-picker-mode-param]")) {
        button.setAttribute("aria-pressed", String((button.dataset.colorPickerModeParam === "gradient") === this.gradient));
      }
    }
    this.solidTarget.hidden = this.gradient;
    if (!this.gradient) {
      if (!this.solidTarget.firstElementChild) this.solidTarget.append(this.chipTplTarget.content.firstElementChild.cloneNode(true));
      this.renderChip(this.solidTarget.firstElementChild, this.stops[0], 0);
    }
    if (this.hasGradientOnlyTarget) {
      this.gradientOnlyTarget.hidden = !this.gradient;
      if (this.gradient) this.renderGradient();
    }
    this.renderPalette();
    if (this.hasNativeTarget) this.nativeTarget.value = this.stops[this.active].hex;
    this.renderOpenButtons();
  }

  /** Un champ : la pastille, l'hex (ou « Aucune »), l'opacité — sans toucher à une valeur
   *  qu'on est en train de taper. */
  renderChip(chip, stop, index) {
    const none = this.transparent && !this.gradient;
    const dot = chip.querySelector("[data-role=dot]");
    dot.style.background = none ? CHECKER : stop.hex;
    dot.querySelector("[data-role=none]").hidden = !none;
    const label = chip.querySelector("[data-role=label]");
    label.dataset.kind = none ? "word" : "hex";
    label.textContent = none ? t("color_picker.none") : stop.hex.slice(1);
    const alpha = chip.querySelector("[data-role=alpha]");
    if (alpha && document.activeElement !== alpha) {
      alpha.value = String(Math.round(stop.alpha * 100));
      alpha.size = Math.max(1, alpha.value.length);
    }
  }

  /** Le bouton contre lequel le popover est ouvert est le seul « expanded ». */
  renderOpenButtons() {
    for (const button of this.element.querySelectorAll("[data-role=open]")) {
      button.setAttribute("aria-expanded", String(this.panelOpen && button === this.anchor));
    }
  }

  renderGradient() {
    const sorted = this.sortedStops();
    this.barTarget.style.background = gradientCss({ angle: 90, stops: sorted.map(({ hex, alpha, offset }) => ({ color: withAlpha(hex, alpha), offset })) });
    this.syncClones(this.barTarget, "[data-handle]", this.handleTplTarget);
    this.barTarget.querySelectorAll("[data-handle]").forEach((handle, index) => {
      const stop = this.stops[index];
      handle.style.left = `${Math.round(stop.offset * 100)}%`;
      handle.dataset.active = String(index === this.active);
      handle.setAttribute("aria-label", `${t("color_picker.position")} ${index + 1} · ${Math.round(stop.offset * 100)} %`);
    });

    this.syncClones(this.stopsTarget, "[data-stop]", this.rowTplTarget);
    this.stopsTarget.querySelectorAll("[data-stop]").forEach((row, index) => {
      const stop = this.stops[index];
      row.dataset.active = String(index === this.active);
      const pos = row.querySelector("[data-role=pos]");
      if (document.activeElement !== pos) {
        pos.value = String(Math.round(stop.offset * 100));
        pos.size = Math.max(1, pos.value.length);
      }
      this.renderChip(row.querySelector("[data-chip]"), stop, index);
      row.querySelector("[data-role=remove]").disabled = this.stops.length <= 2;
    });

    if (document.activeElement !== this.angleTarget) {
      this.angleTarget.value = String(this.angle);
      this.angleTarget.size = Math.max(1, this.angleTarget.value.length);
    }
    this.addStopTarget.disabled = this.stops.length >= MAX_STOPS;
  }

  /** Autant de clones que d'arrêts, mis à jour en place tant que leur nombre ne change pas :
   *  un champ qu'on glisse ne doit pas disparaître sous le pointeur. */
  syncClones(container, selector, template) {
    const nodes = container.querySelectorAll(selector);
    if (nodes.length === this.stops.length) return;
    container.querySelectorAll(selector).forEach((node) => node.remove());
    this.stops.forEach((_, index) => {
      const node = template.content.firstElementChild.cloneNode(true);
      node.dataset.index = index;
      container.append(node);
    });
  }

  /** Les groupes du popover : la charte (avec « Aucune » devant, en uni), les neutres, les
   *  dernières choisies. Les points ne sont refaits que si leur liste change ; sinon seul le
   *  point courant bouge. */
  renderPalette() {
    const none = this.allowTransparentValue && !this.gradient;
    const brand = this.paletteValue.map((c) => c.toLowerCase());
    this.renderDots(this.brandGroupTarget.querySelector("[data-role=dots]"), [...(none ? ["transparent"] : []), ...brand]);
    this.brandGroupTarget.hidden = !none && brand.length === 0;
    this.renderDots(this.neutralsTarget, NEUTRALS);
    const recents = this.recents();
    this.renderDots(this.recentsGroupTarget.querySelector("[data-role=dots]"), recents);
    this.recentsGroupTarget.hidden = recents.length === 0;
  }

  renderDots(container, colors) {
    const key = colors.join(" ");
    if (container.dataset.colors !== key) {
      container.dataset.colors = key;
      container.replaceChildren(...colors.map((color) => this.dot(color)));
    }
    const current = this.transparent ? "transparent" : this.stops[this.active].hex;
    for (const dot of container.children) dot.setAttribute("aria-pressed", String(dot.dataset.colorPickerColorParam === current));
  }

  dot(color) {
    const tpl = color === "transparent" ? this.noneTplTarget : this.dotTplTarget;
    const dot = tpl.content.firstElementChild.cloneNode(true);
    dot.dataset.colorPickerColorParam = color;
    if (color !== "transparent") dot.style.background = color;
    const label = this.colorLabel(color);
    dot.setAttribute("aria-label", label);
    dot.dataset.controller = "tooltip";
    dot.dataset.tooltipTextValue = label;
    return dot;
  }

  /** « Orange · #F28C28 », « Transparent » : le nom le plus proche (lib/color_name), et l'hex. */
  colorLabel(color) {
    if (color === "transparent") return t("color_picker.transparent");
    const key = colorNameKey(color);
    const name = key && t(`composition_editor.layers.colors.${key}`);
    const hex = color.toUpperCase();
    return name ? `${name.charAt(0).toUpperCase()}${name.slice(1)} · ${hex}` : hex;
  }

  recents() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  remember(color) {
    const hex = color.toLowerCase();
    const known = [...this.paletteValue, ...NEUTRALS].map((c) => c.toLowerCase());
    if (known.includes(hex)) return;
    const current = this.recents();
    if (current.includes(hex)) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([hex, ...current].slice(0, RECENTS)));
    } catch { /* quota, navigation privée : tant pis pour les récents */ }
  }
}
